import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, mediaUrl } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { SectionHeading, GoldButton } from "@/components/Brand";

const TABS = [
  ["galleries", "account.galleries"],
  ["favorites", "account.favorites"],
  ["downloads", "account.downloads"],
  ["orders", "account.orders"],
  ["uploads", "account.uploads"],
  ["settings", "account.settings"],
];

export default function Account() {
  const { t, path, lang, setLang, localized, formatDate } = useI18n();
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("galleries");
  const [galleries, setGalleries] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [downloads, setDownloads] = useState([]);
  const [uploads, setUploads] = useState([]);

  useEffect(() => {
    if (!loading && !user) navigate(path("login"), { replace: true });
  }, [loading, user, navigate, path]);

  useEffect(() => {
    if (!user) return;
    api.get("/account/galleries").then((r) => setGalleries(r.data)).catch(() => {});
    api.get("/favorites").then((r) => setFavorites(r.data.media || [])).catch(() => {});
    api.get("/account/downloads").then((r) => setDownloads(r.data)).catch(() => {});
    api.get("/account/uploads").then((r) => setUploads(r.data)).catch(() => {});
  }, [user]);

  if (loading || !user) return <p className="p-12 text-sm text-[#5A5752]">{t("common.loading")}</p>;

  return (
    <div className="mx-auto max-w-[1500px] px-4 py-14 sm:px-8 lg:px-12 lg:py-20" data-testid="account-page">
      <SectionHeading label={user.email} title={t("account.title")} sub={`${t("account.role")}: ${user.role}`} />

      <div className="mt-8 flex flex-wrap gap-2" role="tablist">
        {TABS.map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={tab === key}
            data-testid={`account-tab-${key}`}
            onClick={() => setTab(key)}
            className={`min-h-[44px] border px-4 text-sm transition-colors duration-300 ${
              tab === key
                ? "border-[#8F3329] bg-[#8F3329] text-[#FAFAF7]"
                : "border-[#C69A3B]/50 text-[#252525] hover:border-[#8F3329]"
            }`}
          >
            {t(label)}
          </button>
        ))}
        <button
          type="button"
          data-testid="account-logout-btn"
          onClick={async () => {
            await logout();
            navigate(path("home"));
          }}
          className="min-h-[44px] px-4 text-sm text-[#8F3329] underline"
        >
          {t("btn.logout")}
        </button>
      </div>

      <div className="mt-10">
        {tab === "galleries" && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="account-galleries">
            {galleries.length === 0 && <p className="text-sm text-[#5A5752]">{t("events.empty")}</p>}
            {galleries.map((g) => (
              <Link
                key={g.id}
                to={path("events", g.slug)}
                className="border border-[#C69A3B]/30 bg-white p-5 transition-colors duration-300 hover:border-[#8F3329]"
              >
                <p className="label-mono">{formatDate(g.event_date)}</p>
                <h3 className="mt-2 font-serif-display text-lg">{localized(g, "name")}</h3>
              </Link>
            ))}
          </div>
        )}

        {tab === "favorites" && (
          <div className="masonry" data-testid="account-favorites">
            {favorites.length === 0 && <p className="text-sm text-[#5A5752]">{t("account.noFavorites")}</p>}
            {favorites.map((m) => (
              <div key={m.id} className="overflow-hidden border border-[#C69A3B]/25 bg-white">
                <img src={m.external_url || mediaUrl(m, "thumb")} alt={m.filename} loading="lazy" className="w-full" />
              </div>
            ))}
          </div>
        )}

        {tab === "downloads" && (
          <ul className="space-y-2 text-sm" data-testid="account-downloads">
            {downloads.length === 0 && <li className="text-[#5A5752]">{t("account.noDownloads")}</li>}
            {downloads.map((d) => (
              <li key={d.id} className="flex justify-between border-b border-[#D8D3CB] py-2">
                <span>{d.kind}</span>
                <span className="text-[#5A5752]">{String(d.created_at).slice(0, 10)}</span>
              </li>
            ))}
          </ul>
        )}

        {tab === "orders" && (
          <p className="border border-dashed border-[#C69A3B] p-6 text-sm text-[#5A5752]" data-testid="account-orders">
            {t("account.ordersSoon")}
          </p>
        )}

        {tab === "uploads" && (
          <ul className="space-y-2 text-sm" data-testid="account-uploads">
            {uploads.length === 0 && <li className="text-[#5A5752]">{t("account.noUploads")}</li>}
            {uploads.map((u) => (
              <li key={u.id} className="flex justify-between border-b border-[#D8D3CB] py-2">
                <span className="truncate">{u.filename}</span>
                <span className="label-mono">{u.status}</span>
              </li>
            ))}
          </ul>
        )}

        {tab === "settings" && (
          <div className="max-w-md space-y-5" data-testid="account-settings">
            <div>
              <p className="label-mono mb-2">{t("account.language")}</p>
              <div className="flex gap-2">
                {["es", "en"].map((code) => (
                  <button
                    key={code}
                    type="button"
                    data-testid={`account-language-${code}`}
                    onClick={async () => {
                      await api.patch("/auth/preferences", { language: code }).catch(() => {});
                      setLang(code);
                      toast.success(code.toUpperCase());
                    }}
                    className={`min-h-[44px] border px-5 text-sm ${
                      lang === code ? "border-[#8F3329] bg-[#8F3329] text-[#FAFAF7]" : "border-[#C69A3B]/50"
                    }`}
                  >
                    {code.toUpperCase()}
                  </button>
                ))}
              </div>
            </div>
            <p className="text-sm text-[#5A5752]">
              {t("auth.email")}: {user.email}
            </p>
            {user.role === "admin" && (
              <GoldButton as={Link} to={path("admin")} data-testid="account-admin-link">
                {t("nav.admin")}
              </GoldButton>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
